import type { Scope } from './scope';

/**
 * Live hand-motion trait scores derived from MetaMotion accelerometer
 * magnitude.
 *
 *   Tremor      — fraction of motion power in the 4–12 Hz band, the
 *                 physiological hand-tremor range. Near 0 means no
 *                 tremor (motion energy is mostly in low-frequency
 *                 voluntary motion); higher values mean the hand is
 *                 oscillating.
 *   Smoothness  — inverse normalised RMS jerk (rate-of-change of
 *                 acceleration), mapped to [0, 1]. 1 ≈ perfectly
 *                 smooth glide; 0 ≈ jerky / start-stop motion.
 *   Steady     — inverse normalised standard deviation of accel
 *                 magnitude, mapped to [0, 1]. 1 ≈ rock still;
 *                 0 ≈ wildly varying.
 *
 * These are v1 heuristics, not literature-validated formulas. They use
 * the existing `accel:metawear-*` channels in the scope (populated by
 * {@link feedScope}), which carry the magnitude of either raw
 * `accelerometer` (off mode) or gravity-removed `linearAcceleration`
 * (host fusion mode) — both work for these spectral / kinematic ratios.
 *
 * Window: 2 s (100 samples at the 50 Hz IMU rate used by both modes). The
 * Tremor FFT pads to {@link FFT_N} = 256 to keep the radix-2 step cheap.
 */

const FFT_N = 256;
const WINDOW_SEC = 2;

/** Hand-tremor band per the clinical literature on physiological tremor. */
const TREMOR_LO_HZ = 4;
const TREMOR_HI_HZ = 12;

const EPS = 1e-6;

const HANN = new Float64Array(FFT_N);
for (let i = 0; i < FFT_N; i++) {
  HANN[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (FFT_N - 1)));
}
const fftReal = new Float64Array(FFT_N);
const fftImag = new Float64Array(FFT_N);

function fftRadix2(real: Float64Array, imag: Float64Array): void {
  const n = real.length;
  let j = 0;
  for (let i = 1; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      const tr = real[i];
      const ti = imag[i];
      real[i] = real[j];
      imag[i] = imag[j];
      real[j] = tr;
      imag[j] = ti;
    }
  }
  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1;
    const angle = (-2 * Math.PI) / size;
    const wReal = Math.cos(angle);
    const wImag = Math.sin(angle);
    for (let i = 0; i < n; i += size) {
      let cReal = 1;
      let cImag = 0;
      for (let k = 0; k < half; k++) {
        const tReal = cReal * real[i + k + half] - cImag * imag[i + k + half];
        const tImag = cReal * imag[i + k + half] + cImag * real[i + k + half];
        real[i + k + half] = real[i + k] - tReal;
        imag[i + k + half] = imag[i + k] - tImag;
        real[i + k] += tReal;
        imag[i + k] += tImag;
        const nReal = cReal * wReal - cImag * wImag;
        const nImag = cReal * wImag + cImag * wReal;
        cReal = nReal;
        cImag = nImag;
      }
    }
  }
}

export interface HandTraits {
  /** 0..1 fraction of motion power in the 4–12 Hz tremor band. */
  tremor: number;
  /** [0, 1]; higher = smoother (less normalised jerk). */
  smoothness: number;
  /** [0, 1]; higher = steadier (less stddev of accel magnitude). */
  steady: number;
}

/**
 * Compute the three hand-motion trait scores from the most recent
 * {@link WINDOW_SEC} of accel-magnitude samples for each given sensor.
 *
 * `accelSampleRate` should be the actual delivery rate — 50 Hz in both
 * modes. The window-sample count is derived from it; the FFT pads to
 * {@link FFT_N} either way.
 *
 * Returns a per-sensor map, or an entry omitted when that hand doesn't
 * have a full window of samples yet.
 */
export function handIndices(
  scope: Scope,
  sensorIds: string[],
  accelSampleRate: number,
): Record<string, HandTraits> {
  const out: Record<string, HandTraits> = {};
  const windowSamples = Math.round(WINDOW_SEC * accelSampleRate);
  if (windowSamples < 16 || windowSamples > FFT_N) return out;

  for (const sensorId of sensorIds) {
    const series = scope.read(`accel:${sensorId}`);
    if (series.length < windowSamples) continue;
    const start = series.length - windowSamples;

    // De-mean (so the DC bin doesn't dominate the tremor-fraction
    // denominator) and copy into the FFT buffer, padding with zeros
    // and applying the Hann window. The Hann length matches FFT_N
    // because zero-padded tail bins still contribute spectral
    // leakage with a rectangular window.
    let mean = 0;
    for (let i = 0; i < windowSamples; i++) mean += series[start + i];
    mean /= windowSamples;

    for (let i = 0; i < FFT_N; i++) {
      const v = i < windowSamples ? series[start + i] - mean : 0;
      fftReal[i] = v * HANN[i];
      fftImag[i] = 0;
    }
    fftRadix2(fftReal, fftImag);

    // Bin width = accelSampleRate / FFT_N. Walk bins 1..FFT_N/2.
    const binHz = accelSampleRate / FFT_N;
    const loBin = Math.max(1, Math.floor(TREMOR_LO_HZ / binHz));
    const hiBin = Math.min(FFT_N / 2, Math.ceil(TREMOR_HI_HZ / binHz));
    let tremorPower = 0;
    let totalPower = 0;
    for (let k = 1; k < FFT_N / 2; k++) {
      const p = fftReal[k] * fftReal[k] + fftImag[k] * fftImag[k];
      totalPower += p;
      if (k >= loBin && k <= hiBin) tremorPower += p;
    }
    const tremor = totalPower > 0 ? tremorPower / totalPower : 0;

    // Smoothness: normalised RMS jerk. jerk[i] ≈ (a[i] - a[i-1]) * fs.
    // Normalise by RMS accel to get a unit-free measure; map to (0, 1]
    // via 1 / (1 + relativeJerk).
    let accelSq = 0;
    let jerkSq = 0;
    for (let i = 1; i < windowSamples; i++) {
      const a = series[start + i] - mean;
      const aPrev = series[start + i - 1] - mean;
      accelSq += a * a;
      const j = (a - aPrev) * accelSampleRate;
      jerkSq += j * j;
    }
    const accelRms = Math.sqrt(accelSq / (windowSamples - 1));
    const jerkRms = Math.sqrt(jerkSq / (windowSamples - 1));
    // Without normalisation, jerk scales with motion magnitude — a fast
    // smooth motion looks "jerky" and a stationary noisy reading looks
    // "smooth." Normalising by accelRms removes the magnitude bias.
    const relativeJerk = jerkRms / (accelRms + EPS);
    // Tunable: scale factor controls how aggressively jerk maps to
    // (0, 1]. 50 was picked empirically; the goal is a near-still hand
    // reading > 0.8 and a fast jerky motion reading < 0.3.
    const smoothness = 1 / (1 + relativeJerk / 50);

    // Steady: 1 / (1 + stddev). stddev is the same accelRms above
    // since we de-meaned. Mapped to (0, 1] like smoothness.
    const steady = 1 / (1 + accelRms);

    out[sensorId] = { tremor, smoothness, steady };
  }
  return out;
}
